import React, { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Link2, FileText, Eye, EyeOff, Pencil, Check, X, ExternalLink, ClipboardCheck } from "lucide-react";
import { L, Card, SectionTitle, fmtDate } from "./shared";

const EMPTY = { name: "", category: "other", url: "", note: "", month: "", visible_to_client: true };
const REVIEW_OPTS = [["none", "— fără revizuire —"], ["approval", "Necesită aprobare"], ["review", "De revizuit"], ["pending", "În așteptare"]];
const REVIEW_LABEL = { approval: "Necesită aprobare", review: "De revizuit", pending: "În așteptare" };
const RESULT_LABEL = { approved: "Aprobat", rejected: "Respins", reviewed: "Revizuit" };
const REVIEW_CLS = { approval: "bg-amber-50 text-amber-700 border-amber-200", review: "bg-blue-50 text-blue-700 border-blue-200", pending: "bg-yellow-50 text-yellow-700 border-yellow-200" };
const RESULT_CLS = { approved: "bg-emerald-50 text-emerald-700 border-emerald-200", rejected: "bg-red-50 text-red-700 border-red-200", reviewed: "bg-emerald-50 text-emerald-700 border-emerald-200" };

/** Pastilă cu starea de revizuire (setată de staff) sau rezultatul dat de client. */
function ReviewPill({ d }) {
  if (!d.review_status) return null;
  if (d.review_status === "done") {
    return <span className={`inline-flex items-center gap-1 px-2 h-5 rounded-full border text-[11px] font-medium ${RESULT_CLS[d.review_result] || ""}`} data-testid={`doc-review-pill-${d.id}`}>{RESULT_LABEL[d.review_result] || d.review_result} · {d.review_resolved_by}{d.review_resolved_at ? `, ${fmtDate(d.review_resolved_at)}` : ""}</span>;
  }
  return <span className={`inline-flex items-center px-2 h-5 rounded-full border text-[11px] font-medium ${REVIEW_CLS[d.review_status] || ""}`} data-testid={`doc-review-pill-${d.id}`}>{REVIEW_LABEL[d.review_status] || d.review_status}</span>;
}

/** Formular inline: staff alege statusul de revizuire + notă pentru client. */
function ReviewForm({ d, pid, onSaved, onClose }) {
  const [status, setStatus] = useState(d.review_status && d.review_status !== "done" ? d.review_status : "none");
  const [note, setNote] = useState(d.review_note || "");
  const [busy, setBusy] = useState(false);
  async function save() {
    setBusy(true);
    try { await api.put(`/staff/client-config/${pid}/documents/${d.id}/review`, { review_status: status, note }); toast.success(status === "none" ? "Revizuire anulată" : "Clientul a fost notificat"); onSaved(); onClose(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  }
  return (
    <div className="mt-3 grid grid-cols-1 md:grid-cols-[220px_1fr_auto] gap-2 items-center bg-neutral-50 border border-neutral-200 rounded-lg p-3" data-testid={`doc-review-form-${d.id}`}>
      <select value={status} onChange={e => setStatus(e.target.value)} className="inp" data-testid={`doc-review-status-${d.id}`}>{REVIEW_OPTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
      <input value={note} onChange={e => setNote(e.target.value)} className="inp" placeholder="Notă pentru client (opțional) — ex: „Vă rugăm să confirmați până vineri”" disabled={status === "none"} data-testid={`doc-review-note-${d.id}`} />
      <div className="flex gap-1">
        <button onClick={onClose} className="px-3 py-2 text-sm text-ink-muted hover:text-ink">Renunță</button>
        <button onClick={save} disabled={busy} data-testid={`doc-review-save-${d.id}`} className="inline-flex items-center gap-1.5 px-3 py-2 bg-ink text-white text-sm rounded-lg hover:bg-brand disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Salvează</button>
      </div>
    </div>
  );
}

export default function DocumentsTab({ pid, meta }) {
  const [docs, setDocs] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editF, setEditF] = useState(EMPTY);
  const [reviewing, setReviewing] = useState(null);
  const cats = meta?.doc_categories || [["other", "Altele"]];
  const catLabel = (k) => (cats.find(c => c[0] === k) || [])[1] || k;

  async function load() {
    try { const { data } = await api.get(`/staff/client-config/${pid}/documents`); setDocs(data); }
    catch (e) { toast.error(apiErr(e)); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [pid]);

  async function add() {
    if (!f.name.trim()) { toast.error("Denumirea este obligatorie"); return; }
    setBusy(true);
    try { await api.post(`/staff/client-config/${pid}/documents`, f); setF(EMPTY); await load(); toast.success("Document adăugat"); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  }
  async function saveEdit(d) {
    try { await api.put(`/staff/client-config/${pid}/documents/${d.id}`, editF); setEditing(null); await load(); toast.success("Salvat"); }
    catch (e) { toast.error(apiErr(e)); }
  }
  async function toggleVis(d) {
    try { await api.put(`/staff/client-config/${pid}/documents/${d.id}/visibility`, { visible_to_client: d.visible_to_client === false }); await load(); }
    catch (e) { toast.error(apiErr(e)); }
  }
  async function del(d) {
    if (!window.confirm(`Ștergi „${d.name}” din lista clientului?`)) return;
    try { await api.delete(`/staff/client-config/${pid}/documents/${d.id}`); await load(); toast.success("Document șters"); }
    catch (e) { toast.error(apiErr(e)); }
  }

  if (!docs) return <div className="flex items-center gap-2 text-ink-muted"><Loader2 className="h-5 w-5 animate-spin" /> Se încarcă...</div>;

  return (
    <div className="space-y-6" data-testid="cc-documents">
      <Card>
        <SectionTitle sub="Documentele clientului sunt înregistrate ca metadata + link extern (SharePoint, Drive, etc.). Nu se stochează fișiere aici.">Adaugă document (link)</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <L label="Denumire"><input value={f.name} onChange={e => setF({ ...f, name: e.target.value })} data-testid="doc-name" className="inp" placeholder="Ex: Raport lunar — August 2026" /></L>
          <L label="Categorie"><select value={f.category} onChange={e => setF({ ...f, category: e.target.value })} data-testid="doc-cat" className="inp">{cats.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></L>
          <div className="md:col-span-2"><L label="Link (URL)"><input value={f.url} onChange={e => setF({ ...f, url: e.target.value })} data-testid="doc-url" className="inp" placeholder="https://..." /></L></div>
          <L label="Luna / perioada (opțional)"><input type="month" value={f.month} onChange={e => setF({ ...f, month: e.target.value })} className="inp" /></L>
          <L label="Notă (opțional)"><input value={f.note} onChange={e => setF({ ...f, note: e.target.value })} className="inp" /></L>
        </div>
        <button onClick={add} disabled={busy} data-testid="doc-add" className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-ink text-white text-sm rounded-lg hover:bg-brand disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Adaugă
        </button>
      </Card>

      <Card>
        <SectionTitle sub="Documentele ascunse nu apar în portalul clientului.">Documente vizibile clientului ({docs.length})</SectionTitle>
        {docs.length === 0 ? (
          <div className="py-10 text-center text-ink-muted text-sm border border-dashed border-neutral-200 rounded-lg">Niciun document.</div>
        ) : (
          <div className="divide-y divide-neutral-100 border border-neutral-100 rounded-lg overflow-hidden">
            {docs.map(d => (
              <div key={d.id} className={`px-4 py-3 ${d.visible_to_client === false ? "opacity-60" : ""}`} data-testid={`doc-row-${d.id}`}>
                {editing === d.id ? (
                  <div className="grid grid-cols-1 md:grid-cols-[1fr_180px_1fr_auto] gap-2 items-center">
                    <input value={editF.name} onChange={e => setEditF({ ...editF, name: e.target.value })} className="inp" data-testid="doc-edit-name" />
                    <select value={editF.category} onChange={e => setEditF({ ...editF, category: e.target.value })} className="inp">{cats.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
                    <input value={editF.url} onChange={e => setEditF({ ...editF, url: e.target.value })} className="inp" placeholder="https://..." disabled={d.kind !== "link"} />
                    <div className="flex gap-1">
                      <button onClick={() => setEditing(null)} className="p-1.5 text-ink-muted hover:text-ink"><X className="h-4 w-4" /></button>
                      <button onClick={() => saveEdit(d)} className="p-1.5 text-emerald-600" data-testid="doc-edit-save"><Check className="h-4 w-4" /></button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-lg bg-brand/10 text-brand flex items-center justify-center shrink-0">{d.kind === "link" ? <Link2 className="h-4 w-4" /> : <FileText className="h-4 w-4" />}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-ink truncate flex items-center gap-2">{d.name}
                        <ReviewPill d={d} />
                        {d.url && <a href={d.url} target="_blank" rel="noreferrer" className="text-brand hover:underline inline-flex items-center gap-1 text-[11px]"><ExternalLink className="h-3 w-3" /> deschide</a>}
                      </div>
                      <div className="text-[11px] text-ink-muted font-mono">{catLabel(d.category)} · {d.kind === "link" ? "link" : (d.file_type || "").toUpperCase()} · {d.month || fmtDate(d.uploaded_at)}{d.note ? ` · ${d.note}` : ""}{d.review_comment ? ` · Comentariu client: „${d.review_comment}”` : ""}</div>
                    </div>
                    <button onClick={() => setReviewing(reviewing === d.id ? null : d.id)} title="Revizuire / aprobare de către client" className={`p-2 hover:text-ink ${d.review_status && d.review_status !== "done" ? "text-amber-600" : "text-ink-muted"}`} data-testid={`doc-review-${d.id}`}>
                      <ClipboardCheck className="h-4 w-4" />
                    </button>
                    <button onClick={() => toggleVis(d)} title={d.visible_to_client === false ? "Arată clientului" : "Ascunde de client"} className="p-2 text-ink-muted hover:text-ink" data-testid={`doc-vis-${d.id}`}>
                      {d.visible_to_client === false ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                    <button onClick={() => { setEditing(d.id); setEditF({ name: d.name, category: d.category || "other", url: d.url || "", note: d.note || "", month: d.month || "", visible_to_client: d.visible_to_client !== false }); }} className="p-2 text-ink-muted hover:text-ink" data-testid={`doc-edit-${d.id}`}><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => del(d)} className="p-2 text-ink-muted hover:text-red-600" data-testid={`doc-del-${d.id}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                )}
                {reviewing === d.id && editing !== d.id && <ReviewForm d={d} pid={pid} onSaved={load} onClose={() => setReviewing(null)} />}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
